import { create } from 'zustand';
import { Message, ChatSession, FileItem, StepItem } from '../types';

interface ChatState {
    // Messages
    messages: Message[];
    isTyping: boolean;

    // Files
    files: FileItem[];

    // Sessions
    currentSessionId: string | null;
    chatSessions: ChatSession[];
    isLoadingSessions: boolean;

    // Actions
    setMessages: (messages: Message[]) => void;
    addMessage: (message: Message) => void;
    updateMessage: (id: number, updates: Partial<Message>) => void;
    appendMessageStep: (id: number, step: Omit<StepItem, 'seq'>) => void; // 追加后台步骤（自动编号、首次记录计时起点）
    appendMessageText: (id: number, text: string,shouldOverwrite: boolean) => void;
    setIsTyping: (isTyping: boolean) => void;

    // File actions
    setFiles: (files: FileItem[]) => void;
    addFiles: (files: FileItem[]) => void;
    removeFile: (id: number) => void;
    clearFiles: () => void;

    // Session actions
    setCurrentSessionId: (id: string | null) => void;
    setChatSessions: (sessions: ChatSession[]) => void;
    setIsLoadingSessions: (loading: boolean) => void;
    updateSessionName: (sessionId: string, newName: string) => void;  // 添加更新会话标题的方法
    removeSession: (sessionId: string) => void;  // 添加删除会话的方法

    // Clear all chat data
    clearChat: () => void;
}

// 2026-09-18 用户令：开场白气泡下线（"这个不用了"）——首屏只留空会话引导卡（友好性②）。
// messages 初始即空数组；空态判定（引导卡/预填）已在 0918 改为"无用户消息"，不依赖种子。
export const useChatStore = create<ChatState>((set) => ({
    // Initial state
    messages: [],
    isTyping: false,
    files: [],
    currentSessionId: null,
    chatSessions: [],
    isLoadingSessions: false,

    // Message actions
    setMessages: (messages) => set({ messages }),
    addMessage: (message) => set((state) => ({
        messages: [...state.messages, message]
    })),
    appendMessageText: (id: number, text: string, shouldOverwrite: boolean) => set((state) => ({
        messages: state.messages.map(msg =>
            msg.id === id 
                ? { ...msg, text: shouldOverwrite ? text : msg.text + text } 
                : msg
        )
    })),
    
    updateMessage: (id, updates) => set((state) => ({
        messages: state.messages.map(msg =>
            msg.id === id ? { ...msg, ...updates } : msg
        )
    })),
    appendMessageStep: (id, step) => set((state) => ({
        messages: state.messages.map(msg => {
            if (msg.id !== id) return msg;
            const steps = msg.steps || [];
            const next: StepItem = { ...step, seq: steps.length + 1 };
            // 新的 active 步骤到达时，把上一步 active 收尾为 done（不覆盖历史，对齐离线版）
            const updated = steps.map(s =>
                s.status === 'active' ? { ...s, status: 'done' as const } : s
            );
            return {
                ...msg,
                steps: [...updated, next],
                stepStartedAt: msg.stepStartedAt ?? Date.now(),
            };
        })
    })),
    setIsTyping: (isTyping) => set({ isTyping }),

    // File actions
    setFiles: (files) => set({ files }),
    addFiles: (newFiles) => set((state) => ({
        files: [...state.files, ...newFiles]
    })),
    removeFile: (id) => set((state) => ({
        files: state.files.filter(file => file.id !== id)
    })),
    clearFiles: () => set({ files: [] }),

    // Session actions
    setCurrentSessionId: (id) => set({ currentSessionId: id }),
    setChatSessions: (sessions) => set({ chatSessions: sessions }),
    setIsLoadingSessions: (loading) => set({ isLoadingSessions: loading }),
    updateSessionName: (sessionId, newName) => set((state) => ({
        chatSessions: state.chatSessions.map(session =>
            session.id === sessionId ? { ...session, session_name: newName } : session
        )
    })),
    removeSession: (sessionId) => set((state) => ({
        chatSessions: state.chatSessions.filter(session => session.id !== sessionId)
    })),

    // Clear all
    clearChat: () => set({
        messages: [],
        currentSessionId: null,
        files: []
    })
}));